import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { getMinMaxImpoundDueDate } from 'utils/impounds';
import { formatNumberToCurrencyString } from 'utils/number';

const DIFF = 4;

export const validateAmountDueGroup = ({ index, impound, setValidationErrors }) => {
  if (!impound.frequency) {
    return true;
  }

  const { annualAmount } = impound;
  const dueAmounts = _.map(impound.amountDueDates, 'amountDue');

  const diff = annualAmount - Number((_.sum(dueAmounts) ?? 0).toFixed(DIFF));
  const displayedDiff = formatNumberToCurrencyString(Math.abs(diff));
  const displayedStatus = diff > 0 ? 'more' : 'less';

  if (_.isNaN(diff)) {
    return true;
  }

  const isDiff = diff !== 0;

  const annualAmountError = isDiff
    ? `It's ${displayedDiff} ${displayedStatus} than sum of Amount Due`
    : undefined;

  setValidationErrors(prevErrors => ({
    ...prevErrors,
    [`set_impounds[${index}].annualAmount`]: !annualAmount
      ? 'Required field should not be empty'
      : annualAmountError,
  }));

  return !isDiff;
};

export const getAnnualAmountError = ({
  validationErrors,
  impoundFormName,
  shouldApplyHomeownersInsuranceLock,
}) => {
  const annualAmountError = _.get(validationErrors, `${impoundFormName}.annualAmount`);

  if (!shouldApplyHomeownersInsuranceLock) {
    return annualAmountError;
  }

  // Amount Due 1 is read-only for HOI, so its error is surfaced on the field that drives it.
  return (
    annualAmountError || _.get(validationErrors, `${impoundFormName}.amountDueDates[0].amountDue`)
  );
};

export const getMonthsOption = months => ({ name: String(months), value: months });

// Chattel months follow the due-date calculation until the user picks a different value (LEN-130).
export const getIsFollowingCalculatedMonths = ({ monthsCollected, calculatedMonths }) =>
  _.isNil(monthsCollected) || monthsCollected === calculatedMonths;

export const getIsCalculatedMonthsShown = ({
  isEscrowed,
  isOverridden,
  monthsCollected,
  calculatedMonths,
}) =>
  !!isEscrowed &&
  !!isOverridden &&
  !_.isNil(calculatedMonths) &&
  monthsCollected !== calculatedMonths;

export const getCalculatedMonthsDifferenceLabel = ({ monthsCollected, calculatedMonths }) => {
  if (_.isNil(monthsCollected)) {
    return null;
  }

  const difference = monthsCollected - calculatedMonths;

  return difference > 0 ? `+${difference}` : `${difference}`;
};

export const validateDueDateGroup = ({
  impound,
  impoundFormName,
  prepaidInterestCreditFirstPaymentDate,
  setValidationErrors,
}) => {
  if (!impound.frequency || !prepaidInterestCreditFirstPaymentDate) {
    return true;
  }

  const { minDate, maxDate } = getMinMaxImpoundDueDate({
    prepaidInterestCreditFirstPaymentDate,
  });

  const dueDateErrors = _.map(impound.amountDueDates, 'dueDate').reduce(
    (acc, dueDate, dueDateIndex) => {
      const dueDatesFormName = `${impoundFormName}.amountDueDates[${dueDateIndex}].dueDate`;

      const isValid = dayjs(dueDate).isBetween(minDate, maxDate, 'day', '[]');

      const formattedMinDate = minDate.format(COMMON_DATE_FORMAT);
      const formattedMaxDate = maxDate.format(COMMON_DATE_FORMAT);

      const validationError = !isValid
        ? `Out of valid range: [${formattedMinDate}, ${formattedMaxDate}]`
        : undefined;

      acc[dueDatesFormName] = !dueDate ? 'Required field should not be empty' : validationError;

      return acc;
    },
    {},
  );

  setValidationErrors(prevErrors => ({ ...prevErrors, ...dueDateErrors }));

  return !_.some(dueDateErrors, Boolean);
};
